/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    Anchor,
    Body1,
    Caption,
    FilledCard,
    Flex,
    Heading1,
    IconCheckCircle,
    IconLibraryBooks,
    Image,
    List,
    ListItem,
    PinInput,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useTimeout } from '@myphx-engineering/phx-design-system/hooks';

import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { registerTotpApi, verifyTotpSetupApi } from '../../api/totp.api';
import { PageLayout } from '../../components';
import { CONSTANTS } from '../../constants';
import { telemetryInstance } from '../../instrumentation';
import { routes } from '../../routes';
import { getErrorMessage } from '../../utils/get-error-message';

import type { MfaSelectPageState } from './MfaSelectPage';

export const TotpSetupPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { setBackOverrideState } = useNavBar();

    const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>();
    const [secret, setSecret] = useState<string | undefined>();
    const [isCopied, setCopied] = useState(false);
    const [flowId, setFlowId] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [totpError, setTotpError] = useState<string | undefined>();
    const [code, setCode] = useState<string>('');

    const { start: startCopiedTimer } = useTimeout(
        () => setCopied(false),
        3000
    );

    const copySecret = async () => {
        if (!secret) {
            return;
        }

        try {
            await navigator.clipboard.writeText(secret);

            setCopied(true);
            startCopiedTimer();
        } catch {
            // @TODO
        }
    };

    const state = location?.state as MfaSelectPageState;

    // Validate state
    if (!state || !state.kratosSessionToken) {
        navigate({
            pathname: routes.signIn,
            search: window.location.search,
        });

        return null;
    }

    const onVerifySetupClick = async (verificationCode: string) => {
        if (!flowId || code.length !== 6) {
            return;
        }

        telemetryInstance.logEvent({
            name: 'TOTPSetupVerifySubmitted',
            source: location.pathname,
        });
        setErrorMessage(undefined);
        setLoading(true);

        try {
            // Verify TOTP code via backend
            const { success, error } = await verifyTotpSetupApi(
                state.kratosSessionToken || '',
                flowId,
                verificationCode,
                state.phoneNumber
            );

            if (success) {
                telemetryInstance.logEvent({
                    name: 'TOTPSetupSuccess',
                    source: location.pathname,
                });

                // TOTP setup complete! Session is now AAL2 (verified TOTP device)
                const redirectUrl = `${state.callbackUrl}&session=${state.kratosIdentityId}&authMethod=${CONSTANTS.defaultAuth}`;
                window.location.replace(redirectUrl);
            } else {
                // If user is locked out (429), redirect back to sign-in with lockout flag and phone number
                if (error === '429_MaxAttemptReached') {
                    navigate(
                        {
                            pathname: routes.signIn,
                            search: window.location.search,
                        },
                        {
                            state: {
                                lockoutError: true,
                                phoneNumber: state.phoneNumber,
                            },
                        }
                    );
                    return;
                }

                const errorType = error || '400_InvalidCode';

                // If invalid code, check remaining attempts
                if (errorType === '400_InvalidCode') {
                    const lockoutStatus = await checkAccountLockoutApi(
                        state.phoneNumber
                    );
                    setLoading(false);
                    setErrorMessage(
                        getErrorMessage(
                            t,
                            errorType,
                            lockoutStatus.remainingAttempts
                        )
                    );
                    setCode('');
                    telemetryInstance.logError({
                        source: location.pathname,
                        exception: new Error(
                            error || 'TOTP setup verification failed'
                        ),
                    });
                    return;
                }

                setLoading(false);
                setErrorMessage(getErrorMessage(t, errorType));
                setCode('');
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(
                        error || 'TOTP setup verification failed'
                    ),
                });
            }
        } catch (error) {
            setLoading(false);
            setErrorMessage(getErrorMessage(t, '400_InvalidCode'));
            setCode('');
            telemetryInstance.logError({
                source: location.pathname,
                exception:
                    error instanceof Error ? error : new Error(String(error)),
            });
        }
    };

    const onNavigateBack = useCallback(() => {
        navigate(
            {
                pathname: routes.mfaSelect,
                search: window.location.search,
            },
            { state }
        );
    }, [navigate, state]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    useEffect(() => {
        const getTotpSecret = async () => {
            setLoading(true);

            telemetryInstance.logEvent({
                name: 'MFASelectTOTPSetup',
                source: location.pathname,
            });

            const registerResult = await registerTotpApi(
                state.kratosSessionToken!
            );

            if (registerResult.success) {
                setQrCodeUrl(registerResult.qrCodeUrl);
                setSecret(registerResult.secret);
                setFlowId(registerResult.flowId);
            } else {
                // @TODO: display error
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(
                        `TOTP registration failed: ${registerResult.error}`
                    ),
                });
                setTotpError(registerResult.error);
            }

            setLoading(false);
        };

        if (!!totpError || !!secret || loading) {
            return;
        }

        getTotpSecret();
    }, [
        secret,
        loading,
        state.kratosSessionToken,
        location.pathname,
        totpError,
    ]);

    return (
        <PageLayout
            loadingOverlay={loading}
            heading={<Heading1>{t('totpSetupPage.title')}</Heading1>}
        >
            <List type="ordered">
                <ListItem>
                    <Stack gap="xxs" flex={1}>
                        <Body1>{t('totpSetupPage.step1.desc')}</Body1>
                        <List compact>
                            <ListItem>
                                <Body1 bold>
                                    <Trans
                                        i18nKey="totpSetupPage.step1.googleAuthenticator"
                                        components={{
                                            Android: (
                                                <Anchor
                                                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                                                    target="_blank"
                                                />
                                            ),
                                            iOS: (
                                                <Anchor
                                                    href="https://apps.apple.com/app/google-authenticator/id388497605"
                                                    target="_blank"
                                                />
                                            ),
                                        }}
                                    />
                                </Body1>
                            </ListItem>
                            <ListItem>
                                <Body1 bold>
                                    <Trans
                                        i18nKey="totpSetupPage.step1.authy"
                                        components={{
                                            Android: (
                                                <Anchor
                                                    href="https://play.google.com/store/apps/details?id=com.authy.authy"
                                                    target="_blank"
                                                />
                                            ),
                                            iOS: (
                                                <Anchor
                                                    href="https://apps.apple.com/app/authy/id494168017"
                                                    target="_blank"
                                                />
                                            ),
                                        }}
                                    />
                                </Body1>
                            </ListItem>
                            <ListItem>
                                <Body1 bold>
                                    <Trans
                                        i18nKey="totpSetupPage.step1.microsoftAuthenticator"
                                        components={{
                                            Android: (
                                                <Anchor
                                                    href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                                                    target="_blank"
                                                />
                                            ),
                                            iOS: (
                                                <Anchor
                                                    href="https://apps.apple.com/app/microsoft-authenticator/id983156458"
                                                    target="_blank"
                                                />
                                            ),
                                        }}
                                    />
                                </Body1>
                            </ListItem>
                        </List>
                    </Stack>
                </ListItem>

                <ListItem>
                    <Stack gap="xxs" flex={1}>
                        <Body1>{t('totpSetupPage.step2.desc')}</Body1>
                        <Image
                            src={qrCodeUrl}
                            alt="QR Code"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                width: '150px',
                                display: 'block',
                                margin: '0 auto',
                            }}
                        />
                        <FilledCard color="gray">
                            <Flex gap="md" justify="space-between">
                                <Caption>{secret}</Caption>
                                <Anchor onClick={copySecret}>
                                    {isCopied ? (
                                        <IconCheckCircle />
                                    ) : (
                                        <IconLibraryBooks />
                                    )}
                                </Anchor>
                            </Flex>
                        </FilledCard>
                    </Stack>
                </ListItem>

                <ListItem>
                    <Stack gap="xxs" flex={1}>
                        <Body1>{t('totpSetupPage.step3.desc')}</Body1>
                        <PinInput
                            disabled={loading}
                            // oxlint-disable-next-line jsx-a11y/no-autofocus -- tracked in docs/oxlint-violations.md
                            autoFocus
                            oneTimeCode
                            length={6}
                            size="xl"
                            gap="md"
                            placeholder=""
                            value={code}
                            onChange={setCode}
                            onComplete={onVerifySetupClick}
                            error={errorMessage}
                        />
                    </Stack>
                </ListItem>
            </List>
        </PageLayout>
    );
};
